import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Coffee, Loader2, CheckCircle2, AlertTriangle, Plus, Trash2, Save } from "lucide-react";

const SRC_LABEL = { local: ["Appreso", "Learned"], global: ["Globale", "Global"], rule: ["Regola", "Rule"], ai: ["AI", "AI"], manual: ["Manuale", "Manual"], default: ["Predefinito", "Default"], unknown: ["Da verificare", "Review"] };
const STRATEGY_LABEL = {
  non_refundable: { it: "Non Rimborsabile", en: "Non-Refundable", color: "#ef4444" },
  flexible: { it: "Flessibile", en: "Flexible", color: "#16a34a" },
  last_minute: { it: "Last Minute", en: "Last Minute", color: "#0ea5e9" },
  early_booking: { it: "Early Booking", en: "Early Booking", color: "#8b5cf6" },
};

export default function MealPlanReview({ propId, lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [bfTypes, setBfTypes] = useState([]);
  const [savingBf, setSavingBf] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/meal-plans`);
      setData(data);
      setBfTypes(data.breakfast_types || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [propId]);
  useEffect(() => { load(); }, [load]);

  const correct = async (raw, standard) => {
    setBusy(raw);
    try {
      const { data: r } = await api.post(`/properties/${propId}/meal-plans/correct`, { rate_plan_raw: raw, standard });
      setData((d) => ({ ...d, rates: r.rates })); toast.success(L("Corretto e appreso", "Corrected & learned"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(null); }
  };

  const assocBreakfast = async (raw, breakfast_type_id) => {
    setBusy(raw);
    try {
      const { data: r } = await api.post(`/properties/${propId}/meal-plans/breakfast-assoc`, { rate_plan_raw: raw, breakfast_type_id: breakfast_type_id || null });
      setData((d) => ({ ...d, rates: r.rates })); toast.success(L("Colazione associata", "Breakfast linked"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(null); }
  };

  const saveBfTypes = async (types) => {
    setSavingBf(true);
    try {
      const { data: r } = await api.put(`/properties/${propId}/breakfast-types`, { breakfast_types: types });
      setBfTypes(r.breakfast_types || []);
      setData((d) => ({ ...d, breakfast_types: r.breakfast_types || [], rates: r.rates }));
      toast.success(L("Tipi di colazione salvati", "Breakfast types saved"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setSavingBf(false); }
  };

  if (loading) return <div className="bg-card border border-border rounded-lg p-6"><Loader2 className="w-5 h-5 animate-spin inline text-muted-foreground" /></div>;
  if (!data) return null;
  const standards = data.standards || {};
  const opts = ["room_only", "bed_breakfast", "half_board", "full_board", "all_inclusive"];
  const hasBf = (bfTypes || []).length > 0 || !!data.settings?.breakfast_value;

  const setType = (i, k, v) => setBfTypes(bfTypes.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const addType = () => setBfTypes([...(bfTypes || []), { name: "", price: 0 }]);
  const delType = (i) => setBfTypes(bfTypes.filter((_, idx) => idx !== i));

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border rounded-lg p-6" data-testid="meal-plan-review">
      <div className="flex items-center gap-2 mb-1"><Coffee className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold">{L("Trattamenti tariffari (Room Only / B&B)", "Rate meal plans (Room Only / B&B)")}</h2></div>
      <p className="text-sm text-muted-foreground mb-4">{L("Il motore riconosce automaticamente il trattamento di ogni tariffa importata. Correggi se sbagliato: lo imparerà per i prossimi import.", "The engine auto-detects each imported rate's meal plan. Correct any mistake: it will learn it for future imports.")}</p>

      {/* Breakfast types manager */}
      <div className="rounded-lg border border-border bg-muted/30 p-4 mb-5" data-testid="mp-breakfast-types">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h3 className="font-semibold text-sm flex items-center gap-2"><Coffee className="w-4 h-4 text-primary" />{L("Tipi di colazione", "Breakfast types")}</h3>
          <button data-testid="mp-add-breakfast" onClick={addType} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"><Plus className="w-3.5 h-3.5" />{L("Aggiungi tipo", "Add type")}</button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">{L("Inserisci il valore commerciale di ogni colazione (per camera/notte). Poi associala a ciascuna tariffa nella colonna «Colazione»: serve a scomporre il ricavo Room Only vs B&B.", "Enter the commercial value of each breakfast (per room/night). Then link it to each rate in the \"Breakfast\" column: used to split revenue Room Only vs B&B.")}</p>
        {(bfTypes || []).length === 0 ? (
          <p className="text-xs text-muted-foreground italic mb-2">{L("Nessun tipo di colazione. Aggiungine uno (es. «Colazione Base» €5, «Colazione Premium» €9).", "No breakfast type. Add one (e.g. \"Basic breakfast\" €5, \"Premium breakfast\" €9).")}</p>
        ) : (
          <div className="space-y-2 mb-3">
            {bfTypes.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`mp-bf-row-${i}`}>
                <input value={t.name} onChange={(e) => setType(i, "name", e.target.value)} placeholder={L("Nome colazione", "Breakfast name")}
                  data-testid={`mp-bf-name-${i}`} className="flex-1 h-9 rounded-md border border-border bg-background px-2 text-sm focus:border-primary focus:outline-none" />
                <div className="flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">€</span>
                  <input type="number" min="0" step="0.01" value={t.price} onChange={(e) => setType(i, "price", e.target.value)}
                    data-testid={`mp-bf-price-${i}`} className="w-24 h-9 text-right font-data rounded-md border border-border bg-background px-2 text-sm focus:border-primary focus:outline-none" />
                </div>
                <button data-testid={`mp-bf-del-${i}`} onClick={() => delType(i)} className="p-1.5 text-muted-foreground hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
        <button data-testid="mp-bf-save" onClick={() => saveBfTypes(bfTypes)} disabled={savingBf}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60">
          {savingBf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{L("Salva colazioni", "Save breakfasts")}
        </button>
      </div>

      {!hasBf && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 text-sm p-3 mb-4 flex items-center gap-2" data-testid="mp-no-bf-value">
          <AlertTriangle className="w-4 h-4" />{L("Aggiungi almeno un tipo di colazione qui sopra per attivare la scomposizione del ricavo Room Only vs B&B.", "Add at least one breakfast type above to enable the Room Only vs B&B revenue split.")}
        </div>
      )}

      {(data.rates || []).length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="mp-empty">{L("Nessuna tariffa con trattamento nei dati importati. Importa un file che includa il nome/piano tariffario.", "No rate-plan names in the imported data. Import a file that includes the rate/plan name.")}</p>
      ) : (
        <div className="rounded-lg border border-border overflow-x-auto" data-testid="mp-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50"><tr>
              <th className="text-left px-3 py-2">{L("Tariffa PMS", "PMS rate")}</th>
              <th className="text-left px-3 py-2">{L("Trattamento", "Meal plan")}</th>
              <th className="text-left px-3 py-2">{L("Colazione", "Breakfast")}</th>
              <th className="text-left px-3 py-2">{L("Strategia", "Strategy")}</th>
              <th className="text-left px-3 py-2">{L("Fonte", "Source")}</th>
              <th className="text-left px-3 py-2">{L("Conf.", "Conf.")}</th>
              <th className="text-left px-3 py-2">{L("Pren.", "Bkgs")}</th>
            </tr></thead>
            <tbody>
              {data.rates.map((r) => {
                const low = (r.confidence ?? 0) < 0.6;
                const isBB = r.meal_plan === "bed_breakfast";
                return (
                  <tr key={r.rate_plan_raw} className="border-t border-border" data-testid={`mp-row-${r.rate_plan_raw}`}>
                    <td className="px-3 py-2 font-medium">{r.rate_plan_raw}</td>
                    <td className="px-3 py-2">
                      <select value={r.meal_plan || "room_only"} disabled={busy === r.rate_plan_raw}
                        onChange={(e) => correct(r.rate_plan_raw, e.target.value)} data-testid={`mp-select-${r.rate_plan_raw}`}
                        className="h-8 rounded border border-border bg-background px-1.5 text-sm">
                        {opts.map((o) => <option key={o} value={o}>{(standards[o] || {})[lang === "en" ? "en" : "it"] || o}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2" data-testid={`mp-bf-assoc-${r.rate_plan_raw}`}>
                      {(bfTypes || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <select value={r.breakfast_type_id || ""} disabled={busy === r.rate_plan_raw || !isBB}
                          onChange={(e) => assocBreakfast(r.rate_plan_raw, e.target.value)}
                          data-testid={`mp-bf-select-${r.rate_plan_raw}`}
                          className="h-8 rounded border border-border bg-background px-1.5 text-sm disabled:opacity-50"
                          title={!isBB ? L("Disponibile solo per tariffe Bed & Breakfast", "Only for Bed & Breakfast rates") : ""}>
                          <option value="">{L("— nessuna", "— none")}</option>
                          {bfTypes.map((t) => <option key={t.id} value={t.id}>{t.name} (€{t.price})</option>)}
                        </select>
                      )}
                    </td>
                    <td className="px-3 py-2" data-testid={`mp-strategy-${r.rate_plan_raw}`}>
                      {(r.strategy || []).length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {r.strategy.map((s) => {
                            const st = STRATEGY_LABEL[s];
                            return st ? (
                              <span key={s} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                                style={{ backgroundColor: `color-mix(in srgb, ${st.color} 15%, transparent)`, color: st.color }}>
                                {L(st.it, st.en)}
                              </span>
                            ) : <span key={s} className="text-[10px] text-muted-foreground">{s}</span>;
                          })}
                        </div>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">{(SRC_LABEL[r.source] || [r.source])[lang === "en" ? 1 : 0]}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 text-xs font-bold" style={{ color: low ? "#ef4444" : "#16a34a" }}>
                        {low ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}{Math.round((r.confidence ?? 0) * 100)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 font-data">{r.bookings}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
